import { defineStore } from 'pinia'
import { useSurveysStore } from './surveys-store'
import { useStatesStore } from './states-store'
import {
  loadGroupResultsForSurveyAPI,
  loadGroupResultsPerPersonAPI,
  loadQuestionResponsesForSurveyAPI
} from '@/services/results-service'

import { requestTypes } from '@/constants/request-types'
import { statsComputer } from './helpers/results-store-helper'
import {
  trendComputer,
  trendsForTimePeriod
} from './helpers/trend-store-helper'

export const useResultsStore = defineStore('results-store', {
  state: () => ({
    error: {},
    loading: {},
    currentlyLoadedSurveys: [],
    tags: {},
    categories: {},
    questions: {},
    trendTags: {},
    trendCategories: {},
    trendQuestions: {},
    tagsResultsPerPerson: {},
    categoriesResultsPerPerson: {},
    groupResultsResponse: null
  }),
  getters: {
    getTrends: (state) => {
      return {
        tags: state.trendTags,
        categories: state.trendCategories,
        questions: state.trendQuestions
      }
    }
  },
  actions: {
    clear() {
      this.currentlyLoadedSurveys = []
      this.loading = {}
      this.error = {}
      this.questionStats = null
      this.tags = {}
      this.categories = {}
      this.questions = {}
      this.trendTags = {}
      this.trendCategories = {}
      this.trendQuestions = {}
      this.categoriesResultsPerPerson = {}
      this.tagsResultsPerPerson = {}
      this.groupResultsResponse = null
    },
    clearResponsesPerPerson() {
      this.categoriesResultsPerPerson = {}
      this.tagsResultsPerPerson = {}
    },
    clearQuestionResponses() {
      this.questions = {}
    },
    clearCurrentlyLoadedSurveys() {
      this.currentlyLoadedSurveys = []
    },
    setGroupResultsLoading({ loading }) {
      this.loading[requestTypes.TREND_TAGS] = loading
      this.loading[requestTypes.TREND_CATEGORIES] = loading
      this.loading[requestTypes.TREND_QUESTIONS] = loading
      this.loading[requestTypes.CATEGORIES] = loading
      this.loading[requestTypes.TAGS] = loading
      this.loading[requestTypes.QUESTIONS] = loading
    },
    setGroupResultsError({ error }) {
      this.error[requestTypes.TREND_TAGS] = error
      this.error[requestTypes.TREND_CATEGORIES] = error
      this.error[requestTypes.TREND_QUESTIONS] = error
      this.error[requestTypes.CATEGORIES] = error
      this.error[requestTypes.TAGS] = error
      this.error[requestTypes.QUESTIONS] = error
    },
    updateResults({ surveyName }) {
      const surveysStore = useSurveysStore()
      const statesStore = useStatesStore()

      const selectedTimePeriod = statesStore.getSelectedTimePeriod
      const surveyDetails = surveysStore.getSurveyDetails
      if (!surveyDetails) {
        throw new Error('Survey details not loaded')
      }

      handleSurveyResults({
        surveyName,
        allResults: this.groupResultsResponse?.results,
        surveyDetails,
        selectedTimePeriod,
        store: this
      })
    },
    async loadGroupResultsForSurveyAction({
      surveyName,
      groupName,
      aggregate = false,
      oldSurveys,
      selectedAttributeSegment
    }) {
      if (!surveyName || !groupName) {
        throw new Error(
          `Missing params. Survey: ${surveyName}. Group: ${groupName}.`
        )
      }

      this.setGroupResultsLoading({ loading: true })
      try {
        this.groupResultsResponse = await loadGroupResultsForSurveyAPI({
          surveyName,
          groupName,
          aggregate,
          oldSurveys,
          selectedAttributeSegment
        })

        this.updateResults({ surveyName })

        this.currentlyLoadedSurveys = [surveyName].concat(oldSurveys)
      } catch (error) {
        console.error('error', error)
        this.setGroupResultsError({ error })
        throw error
      } finally {
        this.setGroupResultsLoading({ loading: false })
      }
    },
    async loadGroupResultsPerPerson({
      surveyName,
      groupName,
      statItemName,
      requestType,
      aggregate = false,
      oldSurveys,
      selectedAttributeSegment
    }) {
      this.loading[requestType] = true
      try {
        const response = await loadGroupResultsPerPersonAPI({
          surveyName,
          groupName,
          statItemName,
          aggregate,
          oldSurveys,
          selectedAttributeSegment
        })
        const resultsPerPerson =
          requestType === requestTypes.TAGS_RESULTS_PER_PERSON
            ? { ...this.tagsResultsPerPerson }
            : { ...this.categoriesResultsPerPerson }
        resultsPerPerson[statItemName] = response
        if (requestType === requestTypes.TAGS_RESULTS_PER_PERSON) {
          this.tagsResultsPerPerson = resultsPerPerson
        } else {
          this.categoriesResultsPerPerson = resultsPerPerson
        }
      } catch (error) {
        this.error[requestType] = error
        throw error
      } finally {
        this.loading[requestType] = false
      }
    },
    async loadQuestionResponsesForSurveyAction({
      surveyName,
      groupName,
      categoryName,
      questionName,
      aggregate,
      selectedAttributeSegment
    }) {
      this.loading[requestTypes.QUESTION_RESULTS_FOR_SURVEY] = questionName
      try {
        const response = await loadQuestionResponsesForSurveyAPI({
          surveyName,
          groupName,
          categoryName,
          questionName,
          aggregate,
          selectedAttributeSegment
        })
        if (
          response.error ||
          (Array.isArray(response) && response.length === 0)
        ) {
          const updatedQuestionsWithError = {
            ...this.questions,
            [questionName]: {
              ...this.questions[questionName],
              error: response.error || { code: 'NOT_ENOUGH_RESPONSES' }
            }
          }
          this.questions = updatedQuestionsWithError
        } else {
          const freetextAnswers = response
            .filter((answer) => answer.value !== 'null')
            .map((answer) => ({
              value: answer.value,
              date: answer.date_modified,
              compareDate: new Date(answer.date_modified)
            }))
            .sort((a, b) => b.compareDate - a.compareDate)
          this.questions = {
            ...this.questions,
            [questionName]: {
              ...this.questions[questionName],
              answers: freetextAnswers
            }
          }
        }
      } catch (error) {
        this.error[requestTypes.QUESTION_RESULTS_FOR_SURVEY] = error
        throw error
      } finally {
        this.loading[requestTypes.QUESTION_RESULTS_FOR_SURVEY] = false
      }
    }
  }
})

const handleSurveyResults = ({
  surveyName,
  allResults,
  surveyDetails,
  selectedTimePeriod = null,
  store
  // selectedAttributeSegment = null,
}) => {
  const currentSurveyResults = allResults[surveyName]

  const currentSurveyResultsCopy = currentSurveyResults
  if (Object.keys(currentSurveyResultsCopy || {}).length === 0) {
    store.tags = {}
    store.categories = {}
    store.questions = {}
    store.trendTags = {}
    store.trendCategories = {}
    store.trendQuestions = {}
    return
  }
  const { tags, categories, questions } = statsComputer({
    results: currentSurveyResultsCopy,
    surveyDetails
  })
  store.tags = tags
  store.categories = categories
  store.questions = questions
  // Object.keys(currentSurveyResultsCopy).forEach((type) => {
  //   if (currentSurveyResultsCopy[type]) {
  //     store[type] = statsFormatter({
  //       stats: currentSurveyResultsCopy[type],
  //       type,
  //       surveyDetails
  //     })
  //   }
  // })

  // for generating test data
  // console.l o g('allResults', JSON.stringify(allResults))
  const {
    tags: trendTags,
    categories: trendCategories,
    questions: trendQuestions
  } = trendComputer({
    allResults,
    surveyName,
    surveyDetails
  })

  const trendProps = {
    surveyName,
    surveyDetails,
    selectedTimePeriod
  }

  // TODO: refactor and make just one call to trendsForTimePeriod
  store.trendTags = trendsForTimePeriod({
    trends: {
      tags: trendTags
    },
    ...trendProps
  }).tags

  store.trendCategories = trendsForTimePeriod({
    trends: {
      categories: trendCategories
    },
    ...trendProps
  }).categories

  store.trendQuestions = trendsForTimePeriod({
    trends: {
      questions: trendQuestions
    },
    ...trendProps
  }).questions
}
